// explain.jsx — Simple mode + tap-to-explain.
//
//   useSimple()            → { simple, showEffort, setSimple, setShowEffort }
//                            simple: plain words, effort columns hidden unless
//                            showEffort (the opt-in), one "How did it feel?"
//                            instead of per-muscle check-ins.
//   <Explain id="rpe" />   → a small (?) that opens the term's explanation
//   <Term id="working-max" /> → the term's name for the current mode + (?)
//   <GlossarySheet open onClose /> → every term, plain and lifter names

import React from 'react';
import { useApp } from './store.jsx';
import { LL, LLSwitchTrack } from './tokens.jsx';
import { LLSheet } from './sheets.jsx';
import { GLOSSARY, termLabel, simpleDefault } from './glossary.js';

export function useSimple() {
  const { state, actions } = useApp();
  const simple = simpleDefault(state);
  const showEffort = !simple || state.prefs?.simpleEffort === true;
  return {
    simple,
    showEffort,
    setSimple: (v) => actions.setPref('simpleMode', !!v),
    setShowEffort: (v) => actions.setPref('simpleEffort', !!v),
  };
}

// The (?) affordance. 24px visual inside a 36px hit area that overlaps the
// text around it with negative margins, so it never pushes a layout apart.
export function Explain({ id, size = 16, label, style }) {
  const [open, setOpen] = React.useState(false);
  const [mounted, setMounted] = React.useState(false); // mount closed, then open: the sheet slides in
  const g = GLOSSARY[id];
  if (!g) return null;
  const show = () => { setMounted(true); requestAnimationFrame(() => requestAnimationFrame(() => setOpen(true))); };
  return (
    <>
      <button type="button" aria-label={label || `What is ${g.plain === g.pro ? g.plain : `${g.plain} (${g.pro})`}?`}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); show(); }}
        style={{
          width: 32, height: 32, margin: '-8px -8px -8px -4px', padding: 0, border: 'none', background: 'transparent',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', verticalAlign: 'middle',
          color: LL.fgDim, flexShrink: 0, ...style,
        }}>
        <span aria-hidden="true" style={{
          width: size, height: size, borderRadius: LL.r.pill, border: `1.5px solid currentColor`,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, lineHeight: 1,
        }}>?</span>
      </button>
      {mounted && <ExplainSheet id={id} open={open} onClose={() => setOpen(false)} />}
    </>
  );
}

export function Term({ id, style, explain = true }) {
  const { simple } = useSimple();
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, ...style }}>
      {termLabel(id, simple)}
      {explain && <Explain id={id} />}
    </span>
  );
}

function ExplainSheet({ id, open, onClose }) {
  const g = GLOSSARY[id];
  const { simple, setSimple } = useSimple();
  const [all, setAll] = React.useState(false);
  if (all) return <GlossarySheet open={open} onClose={onClose} />;
  return (
    <LLSheet open={open} onClose={onClose} title={g.plain} subtitle={g.pro !== g.plain ? `Also called ${g.pro}` : 'Explained'} maxHeight="70%">
      <p style={{ margin: '0 0 10px', fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg, lineHeight: 1.45 }}>{g.short}</p>
      <p style={{ margin: 0, fontFamily: LL.ui, fontSize: LL.fs.body, color: LL.fgMute, lineHeight: 1.55 }}>{g.body}</p>
      <div style={{ display: 'flex', gap: 8, marginTop: 18, flexWrap: 'wrap' }}>
        <button type="button" onClick={() => setAll(true)} style={ghostBtn}>All terms</button>
        <button type="button" onClick={() => setSimple(!simple)} style={ghostBtn} aria-pressed={simple}>
          {simple ? 'Show lifter terms' : 'Use simple mode'}
        </button>
      </div>
    </LLSheet>
  );
}

const ghostBtn = {
  height: 44, padding: '0 16px', borderRadius: LL.r.md, border: `1px solid ${LL.line2}`, background: 'transparent',
  color: LL.fg, fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600, cursor: 'pointer',
};

export function GlossarySheet({ open, onClose }) {
  const { simple, setSimple } = useSimple();
  const [openId, setOpenId] = React.useState(null);
  return (
    <LLSheet open={open} onClose={onClose} title="Training terms" subtitle="Glossary" maxHeight="85%">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', marginBottom: 12, borderRadius: LL.r.md, background: LL.surface2, border: `1px solid ${LL.line}` }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg }}>Simple mode</div>
          <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, marginTop: 2 }}>Plain words across the app</div>
        </div>
        <button type="button" role="switch" aria-checked={simple} aria-label="Simple mode" onClick={() => setSimple(!simple)} style={{ background: 'none', border: 'none', padding: 6, margin: -6, cursor: 'pointer' }}>
          <LLSwitchTrack on={simple} />
        </button>
      </div>
      <div style={{ borderRadius: LL.r.md, border: `1px solid ${LL.line}`, overflow: 'hidden' }}>
        {Object.entries(GLOSSARY).map(([id, g], i) => {
          const on = openId === id;
          return (
            <div key={id} style={{ borderTop: i ? `1px solid ${LL.line}` : 'none', background: LL.surface }}>
              <button type="button" onClick={() => setOpenId(on ? null : id)} aria-expanded={on} style={{
                width: '100%', minHeight: 52, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10,
                background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer',
              }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg }}>{g.plain}</span>
                  {g.pro !== g.plain && <span style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgDim, marginLeft: 8 }}>{g.pro}</span>}
                  <span style={{ display: 'block', fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, marginTop: 2, lineHeight: 1.4 }}>{g.short}</span>
                </span>
                <span aria-hidden="true" style={{ color: LL.fgFaint, transform: on ? 'rotate(90deg)' : 'none', transition: `transform ${LL.motion.base}ms ${LL.ease}`, fontFamily: LL.ui, fontSize: LL.fs.lg }}>›</span>
              </button>
              {on && <div style={{ padding: '0 14px 14px', fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, lineHeight: 1.55 }}>{g.body}</div>}
            </div>
          );
        })}
      </div>
    </LLSheet>
  );
}

// Simple mode's one question at finish (replaces the per-muscle check-ins).
// Same 1–5 scale as the summary's "How did it feel?" (session.feel).
export const FEEL_LABELS = ['Rough', 'Meh', 'Solid', 'Strong', 'Great'];
export function HowDidItFeel({ value, onPick }) {
  return (
    <div role="group" aria-label="How did it feel?" style={{ marginTop: 12, padding: '12px 12px 12px', borderRadius: LL.r.md, background: LL.surface2, border: `1px solid ${LL.line}` }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
        <span style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 700, color: LL.fg }}>How did it feel?</span>
        <span style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgDim }}>Optional</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }}>
        {FEEL_LABELS.map((l, i) => {
          const on = value === i + 1;
          return (
            <button key={l} type="button" aria-pressed={on} onClick={() => onPick(on ? null : i + 1)} style={{
              height: 44, borderRadius: LL.r.sm, cursor: 'pointer', fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 600, padding: 0,
              border: `1px solid ${on ? LL.accent : LL.line}`, background: on ? LL.accentSoft : LL.surface, color: on ? LL.accentText : LL.fgMute,
              transition: `background ${LL.motion.fast}ms ${LL.ease}, border-color ${LL.motion.fast}ms ${LL.ease}`,
            }}>{l}</button>
          );
        })}
      </div>
    </div>
  );
}
